/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import Stepper from '@/Components/Stepper';
import { Button } from '@/Components/ui/button';
import ConfirmationModal from '@/Components/Modals/MultiPoModal/ConfirmationModal';
import InitiateProductionPlanModal from '@/Pages/ProductionPlans/InitiateProductionPlanModal/InitiateProductionPlanModal';
import {
	getButtonConfig,
	getButtonLabel,
	getDisabledReason,
	handleButtonClick,
	isButtonVisible,
} from './createOrdersButtonConfig';
import {
	DEFAULT_FLOW_MODE,
	getStepConfig,
	passesIncludeIf,
} from '@/config/createOrdersStepConfig';
import DrillDownStep, { resetDrillDownCache } from './DrillDownStep';
import FormStep from './FormStep';
import TableStep from './TableStep';
import { runApiFetch } from './createOrdersApiConfig';
import { fetchKeyOf, watchedFields } from './useStepTable';
import { notificationServices } from '@/Services';
import {
	BTN_ICONS,
	CLOSED_CONFIRMATION,
} from './createOrdersUiHelpers';
import { useAuth } from '@/Components/Auth/Login/auth';

const OrderStepFlow = (props) => {
	const {
		modeName = DEFAULT_FLOW_MODE,
		category = {},
		selectedCategoryKeys = [],
		navSelectedStore = {},
		onComplete = () => { },
		onBackToCategories = null,
	} = props;

	const stepConfig = getStepConfig(modeName) || getStepConfig(DEFAULT_FLOW_MODE);
	const reduxDispatch = useDispatch();
	const { appConfig = {} } = useAuth();
	const { configs = {} } = appConfig || {};
	const { sortingType = 'AUTO' } = configs;
	const productList = useSelector(
		(s) => s?.form?.ALL_PRODUCTS?.data?.LIST
	);
	const buildInitialState = () => {
		const base = {
			...stepConfig?.initialState,
			...stepConfig?.extraInitialState,
		};
		const orderTypeField = stepConfig?.fields?.find(
			(f) => f.id === 'orderType'
		);
		if (orderTypeField?.configOptions) {
			const options = configs?.[orderTypeField.configOptions];
			if (options?.length) base.orderType = options[0];
		}
		if (category?.orderType) base.orderType = category.orderType;
		return { ...base, ...(navSelectedStore || {}) };
	};
	const [state, setState] = useState(buildInitialState);
	const [lists, setLists] = useState({});
	const [currentStep, setCurrentStep] = useState(1);
	const [confirmationModal, setConfirmationModal] = useState({
		...CLOSED_CONFIRMATION,
		handleCloseConfirmation: () =>
			setConfirmationModal((prev) => ({ ...prev, ...CLOSED_CONFIRMATION })),
		handleConfirmSave: () =>
			setConfirmationModal((prev) => ({ ...prev, ...CLOSED_CONFIRMATION })),
	});
	const [tableDataByStep, setTableDataByStep] = useState({});
	const [prefetching, setPrefetching] = useState(false);
	const lastPrefetchKeyRef = useRef(null);
	const changeQtyRef = useRef(false);
	const EMPTY_TABLE = { headers: [], rows: [] };
	const tableDataFor = (stepKey) => tableDataByStep[stepKey] || EMPTY_TABLE;
	const makeSetTableData = (stepKey) => (updater) =>
		setTableDataByStep((prev) => {
			const current = prev[stepKey] || EMPTY_TABLE;
			const next = typeof updater === 'function'
				? updater(current)
				: updater;
			return { ...prev, [stepKey]: next };
		});
	useEffect(() => () => resetDrillDownCache(), []);

	const allowedSteps = (stepConfig?.steps || []).filter(
		(s) => passesIncludeIf(s?.includeIf, selectedCategoryKeys)
	);
	const steps = allowedSteps.filter((s) => {
		if (!stepConfig?.prefetch || !s?.hideWhenEmpty) return true;
		const fetched = tableDataByStep[s.key];
		return !fetched || (fetched.rows?.length || 0) > 0;
	});

	const prefetchTables = async (force = false) => {
		if (!stepConfig?.prefetch) return tableDataByStep;
		const watched = [...new Set(
			allowedSteps.flatMap((s) => watchedFields(s))
		)];
		const key = fetchKeyOf(watched, state);
		const alreadyLoaded = Object.keys(tableDataByStep).length > 0;
		if (!force && alreadyLoaded && key === lastPrefetchKeyRef.current) {
			return tableDataByStep;
		}
		lastPrefetchKeyRef.current = key;
		setPrefetching(true);
		try {
			const tables = await runApiFetch(stepConfig.prefetch, {
				state,
				lists,
				productList,
			});
			const next = allowedSteps.reduce((acc, s) => {
				if (s?.tableKey && tables?.[s.tableKey]) {
					acc[s.key] = tables[s.tableKey];
				}
				return acc;
			}, {});
			setTableDataByStep(next);
			return next;
		} catch (error) {
			console.error('Error loading orders:', error);
			notificationServices.generateNotification({
				type: 'error',
				message: 'Could not load the orders. Please retry.',
			});
			return null;
		} finally {
			setPrefetching(false);
		}
	};
	useEffect(() => {
		if (steps.length && currentStep > steps.length) {
			setCurrentStep(steps.length);
		}
	}, [steps.length, currentStep]);

	const activeStepCfg = steps[currentStep - 1] || steps[0];
	const tableData = tableDataFor(activeStepCfg?.key);
	const setTableData = makeSetTableData(activeStepCfg?.key);
	const goToStep = (direction, from = currentStep) => {
		const target = from + direction;
		if (target < 1 || target > allowedSteps.length) return;
		setCurrentStep(target);
	};

	const handleProductionPlanClose = () => {
		onComplete();
	};


	const buttonContext = () => ({
		state,
		tableData,
		tables: tableDataByStep,
		step: activeStepCfg,
		stepConfig,
		lists,
		productList,
		setTableData,
		setConfirmationModal,
		changeQtyRef,
		goToStep,
		prefetchTables,
		isPrefetching: prefetching,
		isLastStep: currentStep >= steps.length,
		dispatch: reduxDispatch,
		handleClose: onComplete,
	});

	const finalLabel = (btn) => {
		let label = btn?.noDefineLabel ? btn?.label : `Create ${btn.label}`;
		if (sortingType === 'MANUAL') {
			label = label.replace(
				'Preview Production Plan',
				'Preview Primary Packaging Orders'
			);
		}
		if (state?.orderType) {
			const type = String(state.orderType).toUpperCase();
			if (type === 'PO') {
				label = label.replace(
					'Sales Order',
					'Secondary Dispatch Order / PO'
				);
			} else if (type === 'CMS') {
				label = label.replace(
					'Sales Order',
					'Primary Dispatch Order / CMS'
				);
			}
		}
		return label;
	};

	const renderStepButton = (btn) => {
		const cfg = getButtonConfig(btn.id);
		const reason = getDisabledReason(btn.id, buttonContext());
		const iconOnRight = cfg?.direction > 0;
		const BtnIcon = BTN_ICONS[btn.icon];
		return (
			<Button
				key={btn.id}
				variant={btn.type}
				className={btn.style}
				disabled={Boolean(reason)}
				title={reason || undefined}
				onClick={() => handleButtonClick(btn, buttonContext())}
			>
				{BtnIcon && !iconOnRight && <BtnIcon className='mr-1.5 size-4' />}
				{getButtonLabel(btn, buttonContext(), finalLabel(btn))}
				{BtnIcon && iconOnRight && <BtnIcon className='ml-1.5 size-4' />}
			</Button>
		);
	};
	const handleDrillDownChanges = (stepKey, changes) => {
		setState((prev) => ({
			...prev,
			drillDown: {
				...(prev.drillDown || {}),
				[stepKey]: changes,
			},
		}));
	};

	const stepButtons = (activeStepCfg?.buttons || [])
		.filter((b) => passesIncludeIf(b?.includeIf, selectedCategoryKeys))
		.filter((b) => isButtonVisible(b.id, buttonContext()));
	const leftButtons = stepButtons.filter((b) => b.position === 'left');
	const rightButtons = stepButtons.filter((b) => b.position !== 'left');
	const disabledReasons = [...new Set(
		stepButtons
			.map((btn) => getDisabledReason(btn.id, buttonContext()))
			.filter(Boolean)
	)];

	const renderFormStep = () => (
		<FormStep
			key={activeStepCfg?.key}
			stepCfg={activeStepCfg}
			stepConfig={stepConfig}
			category={category}
			modeName={modeName}
			state={state}
			lists={lists}
			onStateChange={setState}
			onListsChange={(listKey, data) =>
				setLists((prev) => ({ ...prev, [listKey]: data }))}
		/>
	);

	const renderTableStep = () => (
		<TableStep
			key={activeStepCfg?.key}
			stepCfg={activeStepCfg}
			stepConfig={stepConfig}
			modeName={modeName}
			state={state}
			onStateChange={setState}
			lists={lists}
			productList={productList}
			tableData={tableData}
			setTableData={setTableData}
			changeQtyRef={changeQtyRef}
			setConfirmationModal={setConfirmationModal}
			isLoading={prefetching}
		/>
	);

	const renderDrillDownStep = () => (
		<DrillDownStep
			key={activeStepCfg?.key}
			stepCfg={activeStepCfg}
			buildContext={{ state, lists, productList }}
			onChange={handleDrillDownChanges}
		/>
	);

	const stepTypeRenderers = {
		form: renderFormStep,
		table: renderTableStep,
		drillDown: renderDrillDownStep,
	};

	return (
		<div className='flex h-full min-h-0 flex-col'>
			<ConfirmationModal
				isOpen={confirmationModal?.isOpen}
				heading={confirmationModal?.heading}
				message={confirmationModal?.message}
				onClose={confirmationModal?.handleCloseConfirmation}
				onConfirm={confirmationModal?.handleConfirmSave}
				isAlert={confirmationModal?.isAlert}
				alertType={confirmationModal?.alertType}
				customButton={confirmationModal?.customButton}
				showCrossBtn={confirmationModal?.showCrossBtn}
				isLoading={confirmationModal?.isLoading}
				width={confirmationModal?.width}
			/>
			<InitiateProductionPlanModal onInitiated={handleProductionPlanClose} />
			<div className='shrink-0 border-b border-gray-100 bg-white px-4 py-2'>
				<Stepper
					variant='inline'
					steps={steps.map(({ id, title, subtitle }) => ({ id, title, subtitle }))}
					currentStep={currentStep}
					classes={{
						circleBase: 'w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 transition-all duration-300',
						labelWrap: 'flex flex-col items-start leading-tight ml-2',
						title: 'text-[12px] font-semibold',
						subtitle: 'text-[10px] text-gray-400 font-normal',
						connector: 'w-12 h-[2px] mx-3 bg-gray-200 transition-all duration-300',
					}}
				/>
			</div>
			<div className='min-h-0 flex-1 overflow-auto'>
				{stepTypeRenderers[activeStepCfg?.type]?.() ?? null}
			</div>
			<div className='shrink-0 border-t border-gray-200 bg-white px-4 py-2'>
				{disabledReasons.map((reason) => (
					<p
						key={reason}
						className='mb-1 flex w-full justify-end text-[12px] text-red-500'
					>
						{reason}
					</p>
				))}
				<div className='flex w-full items-center justify-between gap-3'>
					<div className='flex items-center gap-2'>
						{currentStep === 1 && onBackToCategories && (
							<Button
								variant='ghost'
								className='text-gray-600 hover:bg-gray-100'
								onClick={onBackToCategories}
							>
								<ArrowLeft className='mr-1.5 size-4' />
								Back to categories
							</Button>
						)}
						{leftButtons.map(renderStepButton)}
					</div>
					<div className='flex flex-1 items-center justify-end gap-2'>
						{rightButtons.map(renderStepButton)}
					</div>
				</div>
			</div>
		</div>
	);
};
export default OrderStepFlow;
